import React, { useState, useEffect, useCallback } from 'react';
import { customersDbApi } from '../utils/api';
import { formatDateInTimezone } from '../utils/dateUtils';
import StatusTag, { STATUS_LABELS } from './StatusTag';
import { stampClass } from './orderDetailsUI';
import { dialogTitleClass } from './customerDialogUI';
import { ClockIcon, LoaderCircleIcon, PackageIcon, RotateCcwIcon, XIcon } from './icons/LucideIcons';

// Forest dialog pattern (backdrop token, 16px radius, dialog shadow, 17px/600
// title); the list scrolls between a fixed title row and the pager.

const PAGE_SIZE = 10;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return formatDateInTimezone(dateStr, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatStatus = (status) => {
  if (!status) return '';
  return STATUS_LABELS[status] || status.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
};

const CustomerHistoryModal = ({ isOpen, onClose, customerId, customerName, onReorder }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  const fetchOrders = useCallback(async (pageNum) => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await customersDbApi.getOrders(customerId, { page: pageNum, limit: PAGE_SIZE });
      if (response.success) {
        setOrders(response.data.orders);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load order history');
      }
    } catch (err) {
      setError('Failed to load order history');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (isOpen && customerId) {
      setPage(1);
      fetchOrders(1);
    }
  }, [isOpen, customerId, fetchOrders]);

  useEffect(() => {
    if (isOpen && customerId && page > 1) {
      fetchOrders(page);
    }
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleUseOrder = (order) => {
    onReorder(order);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-[color:var(--c-backdrop)] px-4 pt-12"
      onClick={handleBackdropClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-history-title"
        className="flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-6 py-4">
          <h3 id="customer-history-title" className={`flex min-w-0 items-center gap-2 ${dialogTitleClass}`}>
            <ClockIcon size={18} className="flex-shrink-0 text-gray-500" />
            <span className="truncate">Order history — {customerName}</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 grid h-8 w-8 flex-shrink-0 place-items-center rounded-md text-gray-500 hover:bg-gray-50 hover:text-gray-900"
          >
            <XIcon size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-[13px] text-gray-600">
              <LoaderCircleIcon size={18} className="animate-spin" />
              Loading orders…
            </div>
          )}

          {error && !loading && (
            <div className="py-12 text-center">
              <p className="text-[13px] text-danger-fg">{error}</p>
              <button type="button" onClick={() => fetchOrders(page)} className="btn-secondary btn-sm mt-3">
                Try again
              </button>
            </div>
          )}

          {!loading && !error && orders.length === 0 && (
            <div className="empty-state py-12">
              <span className="empty-state-icon"><PackageIcon size={20} /></span>
              <span className="empty-state-title">No previous orders found</span>
            </div>
          )}

          {!loading && !error && orders.length > 0 && (
            <div className="space-y-3">
              {orders.map((order) => (
                <div key={order.id} className="rounded-lg border border-gray-200 p-4">
                  {/* Order header row */}
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="tabular font-semibold text-gray-900">#{order.orderNumber}</span>
                      <StatusTag status={order.status} label={formatStatus(order.status)} />
                      {order.orderSource === 'hana_import' && (
                        <span className={`${stampClass} bg-neutral-bg text-neutral-fg`} title="Imported from Hana POS history">Hana</span>
                      )}
                      <span className="text-[13px] text-gray-500">{formatDate(order.deliveryDate)}</span>
                    </div>
                    <span className="tabular font-semibold text-gray-900">${order.total.toFixed(2)}</span>
                  </div>

                  {/* Recipient + address */}
                  <div className="mb-2 text-[13px] text-gray-600">
                    <span className="font-medium text-gray-900">To:</span> {order.recipientName}
                    {order.address && (
                      <span className="ml-1 text-gray-500">— {order.address}</span>
                    )}
                  </div>

                  {/* Items */}
                  {order.items && order.items.length > 0 && (
                    <div className="mb-3 border-l-2 border-gray-200 pl-2 text-[13px] text-gray-600">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex items-baseline justify-between py-0.5">
                          <span>
                            {item.name}
                            {item.arrangementType && (
                              <span className="ml-1 text-gray-500">({item.arrangementType})</span>
                            )}
                            {item.quantity > 1 && (
                              <span className="ml-1 text-gray-500">x{item.quantity}</span>
                            )}
                          </span>
                          <span className="tabular ml-4 flex-shrink-0 text-gray-500">${item.unitPrice.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Use this order */}
                  <div className="flex justify-end">
                    <button type="button" onClick={() => handleUseOrder(order)} className="btn-secondary btn-sm">
                      <RotateCcwIcon size={13} />
                      Use this order
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with pagination */}
        {pagination && pagination.total > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-6 py-3">
            <span className="text-[13px] text-gray-600">
              Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, pagination.total)} of {pagination.total} orders
            </span>
            <div className="flex gap-2">
              <button type="button" onClick={() => setPage(p => p - 1)} disabled={page <= 1} className="btn-secondary btn-sm">
                Previous
              </button>
              <button type="button" onClick={() => setPage(p => p + 1)} disabled={!pagination.hasMore} className="btn-secondary btn-sm">
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerHistoryModal;
